import './feature-guide-billboard.scss';
import React, { useMemo } from 'react';
import { DyfFeatureGuideContentProps } from '../../../../views/dyf-landing-view/hooks/use-dyf-feature-guide-content';
import { VehicleYmm } from '../../../../models/vehicle-ymm';
import AuthenticationService from '../../../../services/authentication-service/authentication-service';
import { DylLandingProps } from '../../../../views/dyf-landing-view/hooks/use-dyl-landing';
import { StringUtil } from '../../../utils/string-util/string-util';
import SecondaryButton from '../../../common/secondary-button/secondary-button';
import { useVehicleImage } from '../../../../hooks/use-vehicle-image';
import { OnboardingVehicleSelectorButton } from './vehicle-selector-button/vehicle-selector-button';
import { useVehicleSelectorToggle } from '../../../../hooks/dyf/use-vehicle-selector-toggle/use-vehicle-selector-toggle';

interface FeatureGuideLincolnBillboardProps {
    eligibleFeatureGuide?: boolean;
    vehicleYmmGroup?: VehicleYmm;
    vin: string;
    featureGuideContent?: DyfFeatureGuideContentProps;
    isAuthenticated: boolean;
    dylLandingContent?: DylLandingProps | null;
    hasEligibleModelData?: boolean;
    flow: 'vin' | 'ymm';
}

export const FeatureGuideBillboardLincoln = (
    props: FeatureGuideLincolnBillboardProps
) => {
    const {
        hasEligibleModelData,
        flow,
        isAuthenticated,
        vin,
        dylLandingContent,
        eligibleFeatureGuide,
        featureGuideContent,
        vehicleYmmGroup,
    } = props;

    const shouldDisplayVehicleSelector = useVehicleSelectorToggle();

    const { vehicleImage } = useVehicleImage({
        year: vehicleYmmGroup?.year || 0,
        vin: vin || '',
    });

    const replaceYMM = (data: string) => {
        return data
            ?.replace('[year]', vehicleYmmGroup?.year.toString() || '')
            .replace('[make]', vehicleYmmGroup?.make || '')
            ?.replace(
                '[model]',
                StringUtil.capitalizeFirstLetter(vehicleYmmGroup?.model || '')
            );
    };
    const handleLogin = () => {
        const authenticationService = new AuthenticationService();
        if (!isAuthenticated) authenticationService.login();
    };

    const wrapperClassNames = useMemo<string>(() => {
        const classes = ['feature-guide-billboard--wrap'];
        if (!vehicleImage) classes.push('dyf-no-vehicle-image');
        if (!hasEligibleModelData)
            classes.push('lincoln-billboard-margin-bottom');

        return classes.join(' ');
    }, [eligibleFeatureGuide, vin, vehicleImage]);

    const signInAndSelectorClasses = useMemo(() => {
        const classes = ['lincoln-sign-in-and-selector'];
        if (vin) {
            classes.push('vin-alignment');
        }
        return classes.join(' ');
    }, [vin]);

    return (
        <div className={wrapperClassNames}>
            <div
                className={`vehicle-image--wrapper ${
                    flow === 'ymm' ? 'hidden' : ''
                }`}
            >
                {vehicleImage && (
                    <img
                        crossOrigin="anonymous"
                        className={`vehicle-image`}
                        data-testid="vehicle-image"
                        src={vehicleImage}
                        alt={replaceYMM(
                            featureGuideContent?.billboardImageAltText ?? ''
                        )}
                    />
                )}
            </div>
            <div className="billboard-text-wrapper">
                <div className="billboard-title-text">
                    {featureGuideContent?.billboardBody}
                </div>

                <div className="vehicle-title" data-testid="vehicle-title">
                    <h1>
                        {replaceYMM(featureGuideContent?.billboardTitle ?? '')}
                    </h1>
                </div>
                {!hasEligibleModelData &&
                    dylLandingContent &&
                    dylLandingContent.bannerVehicleUnsupportedPageText && (
                        <div className="billboard-body-text">
                            {dylLandingContent.bannerVehicleUnsupportedPageText}
                        </div>
                    )}
                {hasEligibleModelData && !isAuthenticated && dylLandingContent && (
                    <div className={signInAndSelectorClasses}>
                        <SecondaryButton
                            className="dyl-sign-in-button"
                            onClick={handleLogin}
                            noChevron={true}
                            aria-label={
                                dylLandingContent?.bannerSignIntoMyLincolnText
                            }
                        >
                            {dylLandingContent.bannerSignIntoMyLincolnText}
                        </SecondaryButton>
                        {shouldDisplayVehicleSelector && (
                            <OnboardingVehicleSelectorButton
                                lowercaseBrand={'lincoln'}
                                buttonText={
                                    featureGuideContent?.vehicleSelectorButtonText ||
                                    ''
                                }
                                className={'selector-mobile'}
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
